import React, { PropsWithChildren, useContext, useState } from 'react'
import './style/sidebar.scss'
import classnames from 'classnames'
import SidebarCollapseToggle from './sidebar-collapse-toggle'
import UserMenu from './user-menu'
import ActiveDomainSelect from './active-domain-select'
import AppMenu from './app-menu'
import SidebarActionItem from './sidebar-action-item'
import { QuestionCircleFilled } from '@ant-design/icons'
import { IconPushly } from '../svg/icon-pushly'
import { AppState } from '../../stores/app'
import { AppService } from '../../services'
import { Container } from 'typescript-ioc/es5'
import { observer } from 'mobx-react'
import { PlatformUserContext, UserReviewStatus } from '../../providers/user-provider/user-provider'
import { Modal } from 'antd'
import G2ColorIcon from '../icons/g2-icon-color'
import SkeletonAvatar from 'antd/es/skeleton/Avatar'
import { SkeletonBlock } from '@pushly/aqe/lib/components'
import { useService } from '@pushly/aqe/lib/hooks'

interface ISidebarSection {
    section: string
}
const SidebarSection: React.FunctionComponent<PropsWithChildren<ISidebarSection>> = (props) => {
    return (
        <div className={classnames(`sidebar-section-${props.section}`)}>
            <div className={classnames('sidebar-section-content')}>{props.children}</div>
        </div>
    )
}

@observer
class Sidebar extends React.Component {
    public static contextType = PlatformUserContext
    public context!: React.ContextType<typeof PlatformUserContext>

    protected readonly appState: AppState
    protected readonly appService: AppService

    public constructor(props: any) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public render() {
        const { sidebar, currentDomain } = this.appState
        const canRender = this.context.authenticated

        return !canRender ? null : (
            <div
                className={classnames('app-sidebar', this.appState.abilityStore.acs.state, {
                    open: !sidebar.collapsed,
                    collapsed: sidebar.collapsed,
                    'context-org': this.appState.inOrgContext,
                    'context-domain': this.appState.inDomainContext,
                })}
            >
                <SidebarCollapseToggle />

                <SidebarSection section="upper">
                    <ActiveDomainSelect />
                </SidebarSection>

                <SidebarSection section="mid">
                    <AppMenu />
                </SidebarSection>

                <SidebarSection section="lower">
                    <G2ReviewComponent />

                    <SidebarActionItem
                        title="Help"
                        description={false}
                        icon={<QuestionCircleFilled />}
                        iconStyle="fill"
                        href={this.appState.documentationLink}
                    />

                    <UserMenu />
                </SidebarSection>

                <SidebarSection section="lower">
                    <span
                        className={classnames('sidebar-logo')}
                        onClick={() => {
                            if (!!currentDomain) {
                                this.appService.routeWithinDomain('/dashboard')
                            }
                        }}
                    >
                        <span className="sidebar-icon" />
                        <IconPushly className="sidebar-icon pushly-icon" />
                    </span>
                </SidebarSection>
            </div>
        )
    }
}

export default Sidebar

const G2ReviewComponent = () => {
    const userCtx = useContext(PlatformUserContext)
    const appState = useService(AppState)
    const appSvc = useService(AppService)

    if (!userCtx.authenticated) return null

    const [reviewModalOpen, setReviewModalOpen] = useState(false)

    const domainHasReviewFlag = appState.currentDomain?.flags?.includes('FEAT_G2_IN_APP_REVIEW')
    const canShowReviewItem =
        domainHasReviewFlag && !userCtx.user.isInternalUser && userCtx.reviewState.status !== UserReviewStatus.COMPLETED

    if (!canShowReviewItem) return null

    const handleReviewClick = () => {
        const nextStatus = UserReviewStatus.ACKNOWLEDGED

        // only update to acknowledge if the user hasn't already dismissed the banner -- last updated is shared
        if (userCtx.reviewState.status !== UserReviewStatus.DISMISSED) {
            appSvc.setG2ReviewStatus(userCtx.user.id, { status: nextStatus })

            userCtx.assignUser!((prev) => ({
                ...prev,
                reviewState: {
                    ...('reviewState' in prev ? prev.reviewState : {}),
                    status: nextStatus,
                },
            }))
        }
        setReviewModalOpen(true)
    }

    return canShowReviewItem && userCtx.reviewState.loading ? (
        <div className="sidebar-review-loading">
            <SkeletonAvatar size="small" active />
            {!appState.sidebar.collapsed && <SkeletonBlock size="small" active />}
        </div>
    ) : (
        <div>
            <SidebarActionItem
                title="Review us on G2!"
                icon={<G2ColorIcon className="sidebar-icon" />}
                onClick={handleReviewClick}
            />

            <Modal
                className="g2-review-modal"
                open={reviewModalOpen}
                onCancel={() => setReviewModalOpen(false)}
                closable={true}
                destroyOnClose={true}
                footer={null}
            >
                <iframe src={userCtx.reviewState.url} />
            </Modal>
        </div>
    )
}
